// ═══════════════════════════════════════════════════════════════
// HotWash — AI Summary Panel
// Small auto-generated assessment widget (heuristic, no API call)
// ═══════════════════════════════════════════════════════════════

import { Paper, Group, Text, RingProgress, Center, SimpleGrid, Badge } from '../gab/GabComponents';
import { IconSparkles } from '@tabler/icons-react';
import type { Observation } from '../../types';
import { primary, semantic } from '../../lib/colors';

interface Props {
  observations: Observation[];
}

function getScore(obs: Observation[]): { score: number; label: string; color: string } {
  if (obs.length === 0) return { score: 0, label: 'No Data', color: '#939598' };
  const total = obs.length;
  const critical = obs.filter(o => o.tier === 'critical_error').length;
  const problems = obs.filter(o => o.tier === 'problem_identified').length;
  const withRemedy = obs.filter(o => o.suggestedRemedy).length;

  let score = 10;
  score -= (critical / total) * 5;
  score -= (problems / total) * 2.5;
  score += (withRemedy / total) * 1.5;
  score = Math.max(1, Math.min(10, Math.round(score * 10) / 10));

  if (score >= 8) return { score, label: 'Strong', color: semantic.success };
  if (score >= 6) return { score, label: 'Adequate', color: semantic.info };
  if (score >= 4) return { score, label: 'Needs Improvement', color: semantic.warning };
  return { score, label: 'Critical', color: semantic.error };
}

export default function AISummaryPanel({ observations }: Props) {
  if (observations.length === 0) return null;

  const { score, label, color } = getScore(observations);
  const total = observations.length;
  const critical = observations.filter(o => o.tier === 'critical_error').length;
  const withRemedy = observations.filter(o => o.suggestedRemedy).length;
  const remedyPct = Math.round((withRemedy / total) * 100);
  const criticalPct = Math.round((critical / total) * 100);

  // Top category
  const catCounts: Record<string, number> = {};
  observations.forEach(o => { catCounts[o.category] = (catCounts[o.category] || 0) + 1; });
  const topCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';

  // Top phase
  const phaseCounts: Record<string, number> = {};
  observations.forEach(o => { phaseCounts[o.phase] = (phaseCounts[o.phase] || 0) + 1; });
  const topPhase = Object.entries(phaseCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';

  return (
    <Paper p="sm" radius="sm" withBorder>
      <Group justify="space-between" mb={4}>
        <Group gap="xs">
          <IconSparkles size={14} color={primary[700]} />
          <Text fw={600} size="xs">Assessment Score</Text>
        </Group>
        <Badge size="xs" variant="light" color="#939598">Heuristic</Badge>
      </Group>

      <Group gap="md" align="flex-start">
        <RingProgress
          size={72}
          thickness={6}
          roundCaps
          sections={[{ value: score * 10, color }]}
          label={
            <Center>
              <div style={{ textAlign: 'center' }}>
                <Text fw={700} fz={18} style={{ color, lineHeight: 1 }}>{score}</Text>
                <Text fz={9} c="dimmed">/10</Text>
              </div>
            </Center>
          }
        />
        <div style={{ flex: 1 }}>
          <Badge size="sm" mb="xs" style={{ background: color + '15', color, border: `1px solid ${color}30` }}>
            {label}
          </Badge>
          <Text size="xs" c="dimmed">
            {total} observation{total !== 1 ? 's' : ''} analyzed.
            {critical > 0 ? ` ${critical} critical issue${critical !== 1 ? 's' : ''} identified.` : ''}
            {remedyPct >= 70 ? ' Good remedy coverage.' : remedyPct >= 40 ? ' Moderate remedy coverage.' : ' Low remedy coverage — consider adding remedies.'}
          </Text>
        </div>
      </Group>

      <SimpleGrid cols={4} mt="sm">
        <div>
          <Text fz={10} c="dimmed">Critical Rate</Text>
          <Text fw={600} size="sm" style={{ color: criticalPct > 20 ? 'var(--color-error)' : undefined }}>{criticalPct}%</Text>
        </div>
        <div>
          <Text fz={10} c="dimmed">Remedied</Text>
          <Text fw={600} size="sm" style={{ color: semantic.success }}>{remedyPct}%</Text>
        </div>
        <div>
          <Text fz={10} c="dimmed">Top Category</Text>
          <Text fw={600} size="sm" tt="capitalize">{topCat}</Text>
        </div>
        <div>
          <Text fz={10} c="dimmed">Top Phase</Text>
          <Text fw={600} size="sm" tt="capitalize">{topPhase}</Text>
        </div>
      </SimpleGrid>
    </Paper>
  );
}
